(() => {
  const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

  const parseCities = (el) => {
    try {
      const raw = el.getAttribute('data-cities');
      const cities = JSON.parse(raw || '[]');
      return cities.filter((c) => c && c.name && Number.isFinite(c.lat) && Number.isFinite(c.lng));
    } catch {
      return [];
    }
  };

  const fitCities = (map, cities, { animate = false, duration = 900 } = {}) => {
    if (!cities.length) {
      map.setCenter([20, 25]);
      map.setZoom(1.2);
      return;
    }
    if (cities.length === 1) {
      if (animate) {
        map.flyTo({
          center: [cities[0].lng, cities[0].lat],
          zoom: 3.5,
          essential: true,
          duration,
        });
      } else {
        map.setCenter([cities[0].lng, cities[0].lat]);
        map.setZoom(3.5);
      }
      return;
    }
    const bounds = new maplibregl.LngLatBounds();
    cities.forEach((c) => bounds.extend([c.lng, c.lat]));
    map.fitBounds(bounds, {
      padding: { top: 72, bottom: 64, left: 72, right: 110 },
      maxZoom: 3.8,
      duration: animate ? duration : 0,
      essential: true,
    });
  };

  // Fan labels away from neighbors so regional clusters (Istanbul/Tel Aviv, Bangkok/Suzhou/Tokyo) don't collide
  const assignLabelSides = (cities) => {
    const sides = cities.map(() => 'top');
    const CLOSE = 28; // degrees — Asia trio + Istanbul/Tel Aviv at world zoom
    const n = cities.length;
    if (n < 2) return sides;

    const maxLng = Math.max(...cities.map((c) => c.lng));
    const minLng = Math.min(...cities.map((c) => c.lng));

    const dist = (i, j) =>
      Math.hypot(cities[i].lat - cities[j].lat, cities[i].lng - cities[j].lng);

    // Connected clusters of nearby cities
    const visited = new Array(n).fill(false);
    const clusters = [];
    for (let i = 0; i < n; i++) {
      if (visited[i]) continue;
      const cluster = [];
      const stack = [i];
      visited[i] = true;
      while (stack.length) {
        const cur = stack.pop();
        cluster.push(cur);
        for (let j = 0; j < n; j++) {
          if (!visited[j] && dist(cur, j) <= CLOSE) {
            visited[j] = true;
            stack.push(j);
          }
        }
      }
      clusters.push(cluster);
    }

    const angleToSide = (angle) => {
      // atan2(lng, lat): 0 = north, +π/2 = east
      const a = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      if (a < Math.PI * 0.25 || a >= Math.PI * 1.75) return 'top';
      if (a < Math.PI * 0.75) return 'right';
      if (a < Math.PI * 1.25) return 'bottom';
      return 'left';
    };

    // Avoid sides that hang off the map (Tokyo + right, New York + left)
    const pickInBounds = (i, preferred, used) => {
      const blocked = new Set(used);
      if (cities[i].lng >= maxLng - 1e-6) blocked.add('right');
      if (cities[i].lng <= minLng + 1e-6) blocked.add('left');

      for (const side of [preferred, 'top', 'bottom', 'left', 'right']) {
        if (!blocked.has(side)) return side;
      }
      return preferred;
    };

    clusters.forEach((cluster) => {
      if (cluster.length < 2) return;

      // Pairs: always opposite sides along the geographic axis (Istanbul / Tel Aviv)
      if (cluster.length === 2) {
        const [i, j] = cluster;
        const dLat = cities[i].lat - cities[j].lat;
        const dLng = cities[i].lng - cities[j].lng;
        if (Math.abs(dLat) >= Math.abs(dLng)) {
          sides[i] = dLat >= 0 ? 'top' : 'bottom';
          sides[j] = dLat >= 0 ? 'bottom' : 'top';
        } else {
          sides[i] = pickInBounds(i, dLng >= 0 ? 'right' : 'left', new Set());
          sides[j] = pickInBounds(j, dLng >= 0 ? 'left' : 'right', new Set([sides[i]]));
        }
        return;
      }

      // 3+ cities: place each label outward from the cluster centroid
      const cx = cluster.reduce((s, i) => s + cities[i].lng, 0) / cluster.length;
      const cy = cluster.reduce((s, i) => s + cities[i].lat, 0) / cluster.length;
      const ranked = cluster
        .map((i) => ({
          i,
          angle: Math.atan2(cities[i].lng - cx, cities[i].lat - cy),
        }))
        .sort((a, b) => a.angle - b.angle);

      const used = new Set();
      ranked.forEach(({ i, angle }) => {
        const side = pickInBounds(i, angleToSide(angle), used);
        sides[i] = side;
        used.add(side);
      });
    });

    return sides;
  };

  const buildMarker = (city, index, side = 'top') => {
    const root = document.createElement('button');
    root.type = 'button';
    root.className = 'mg-maplibre-marker';
    root.style.setProperty('--mg-pin-delay', `${0.12 + index * 0.1}s`);
    root.setAttribute('aria-label', city.name);
    root.innerHTML = `
      <span class="mg-maplibre-marker__inner">
        <span class="mg-maplibre-marker__pulse" aria-hidden="true"></span>
        <span class="mg-maplibre-marker__dot" aria-hidden="true"></span>
        <span class="mg-maplibre-marker__label mg-maplibre-marker__label--${side}">${city.name}</span>
      </span>
    `;
    return root;
  };

  const initMap = (wrap) => {
    if (wrap.dataset.mgMapReady === 'true' || typeof maplibregl === 'undefined') return;

    const canvas = wrap.querySelector('[data-mg-map-canvas]');
    if (!canvas) return;

    const cities = parseCities(wrap);
    wrap.dataset.mgMapReady = 'true';

    const map = new maplibregl.Map({
      container: canvas,
      style: STYLE_URL,
      center: [20, 25],
      zoom: 1.15,
      attributionControl: false,
      interactive: true,
      dragRotate: false,
      pitchWithRotate: false,
      monochrome: false,
    });

    map.scrollZoom.disable();
    map.dragPan.disable();

    const markers = [];

    map.on('load', () => {
      // Hide basemap place / road labels — only MedGlobe pin labels remain
      const style = map.getStyle();
      style.layers?.forEach((layer) => {
        if (layer.type === 'symbol') {
          try {
            map.setLayoutProperty(layer.id, 'visibility', 'none');
          } catch {
            /* ignore layers we can't edit */
          }
        }
      });

      if (!map.getLayer('mg-water-tint') && map.getLayer('water')) {
        try {
          map.setPaintProperty('water', 'fill-color', '#d7e7f7');
        } catch {
          /* style variants differ */
        }
      }

      fitCities(map, cities);

      const labelSides = assignLabelSides(cities);

      cities.forEach((city, index) => {
        const el = buildMarker(city, index, labelSides[index]);
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([city.lng, city.lat])
          .addTo(map);
        markers.push({ city, marker, el });

        el.addEventListener('click', (event) => {
          event.stopPropagation();
          map.flyTo({
            center: [city.lng, city.lat],
            zoom: Math.max(map.getZoom(), 3.6),
            essential: true,
            duration: 900,
          });
          markers.forEach((m) => m.el.classList.toggle('is-active', m.el === el));
        });
      });

      // Click empty map → zoom back out to all cities
      map.on('click', () => {
        markers.forEach((m) => m.el.classList.remove('is-active'));
        fitCities(map, cities, { animate: true, duration: 900 });
      });

      const settle = () => {
        map.resize();
        fitCities(map, cities);
        wrap.classList.add('is-map-ready');
      };

      requestAnimationFrame(() => {
        settle();
        setTimeout(settle, 120);
      });
    });

    // City list → fly to pin
    const section = wrap.closest('.mg-presence') || wrap.closest('section');
    section?.querySelectorAll('[data-mg-map-city]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('data-mg-map-city');
        const match = markers.find((m) => m.city.name === name);
        if (!match) return;
        match.el.click();
      });
    });

    const resize = () => map.resize();
    window.addEventListener('resize', resize, { passive: true });

    // Reveal animation may start at opacity 0 — resize when visible
    if (!wrap.classList.contains('is-inview')) {
      const mo = new MutationObserver(() => {
        if (!wrap.classList.contains('is-inview')) return;
        resize();
        fitCities(map, cities);
        mo.disconnect();
      });
      mo.observe(wrap, { attributes: true, attributeFilter: ['class'] });
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-mg-map]').forEach(initMap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-mg-map]').forEach((el) => {
      el.dataset.mgMapReady = '';
      initMap(el);
    });
  });
})();
